import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from '../components/AppIcon';

// Pages that are always accessible (no trial/subscription check)
const ALWAYS_ALLOWED_PATHS = ['/billing', '/onboarding', '/paywall', '/login', '/signup'];

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, loading, organization, profile } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Icon name="Zap" size={24} color="white" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <p className="text-muted-foreground text-sm">Loading Wilcart CRM...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Super admin guard
  if (requireAdmin && !profile?.is_platform_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  // ✅ Super admins (platform admins) bypass ALL billing/trial checks
  if (profile?.is_platform_admin) {
    return children;
  }

  // Onboarding guard — must complete onboarding first
  // Skip for invited users who already belong to an org (they don't create a new org)
  if (
    organization &&
    (organization?.onboarding_completed === false || organization?.onboarding_completed === null || organization?.onboarding_completed === undefined) &&
    typeof window !== 'undefined' &&
    window.location?.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // If user has an org_id but org data hasn't loaded yet, don't redirect to onboarding
  // This handles invited users whose org loads asynchronously.
  // We only bypass for users who are NOT the org creator (invited members joining existing orgs).
  // New org creators will have their org load quickly and then be redirected to onboarding.
  if (
    !organization &&
    profile?.org_id &&
    profile?.role === 'member' &&
    typeof window !== 'undefined' &&
    window.location?.pathname !== '/onboarding'
  ) {
    // org is still loading — let it through, don't redirect
    return children;
  }

  // Trial / subscription access control
  // Skip check for always-allowed paths
  const currentPath = location?.pathname;
  const isAlwaysAllowed = ALWAYS_ALLOWED_PATHS?.some((p) => currentPath?.startsWith(p));

  if (organization && !isAlwaysAllowed) {
    const subscriptionStatus = organization?.subscription_status;
    const trialEndDate = organization?.trial_end_date;

    // No status yet = brand new user still setting up → allow through
    if (!subscriptionStatus) {
      return children;
    }

    // 'active' or 'trialing' (Stripe uses 'trialing' for free-month promo subscriptions)
    const isSubscriptionActive =
      subscriptionStatus === 'active' || subscriptionStatus === 'trialing';

    let isTrialActive = false;
    if (subscriptionStatus === 'trial' && trialEndDate) {
      isTrialActive = new Date(trialEndDate) > new Date();
    }

    // If neither trial nor subscription is active → redirect to paywall
    if (!isSubscriptionActive && !isTrialActive) {
      const isDefinitelyExpired =
        subscriptionStatus === 'expired' ||
        subscriptionStatus === 'canceled' ||
        subscriptionStatus === 'past_due' ||
        (subscriptionStatus === 'trial' && trialEndDate && new Date(trialEndDate) <= new Date());

      if (isDefinitelyExpired) {
        return <Navigate to="/paywall" replace />;
      }
    }
  }

  return children;
};

export default ProtectedRoute;
